import type {Component} from 'grapesjs';
import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {isVideoMediaComponent} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {makeInfoBoxControl} from '../../controls/info-box-control/info-box-control';
import {makeOptionListControl} from '../../controls/option-list-control/option-list-control';
import {horizontalAlignDescriptor} from '../../controls/option-list-control/option-list-control-descriptors';
import {makeSliderControl} from '../../controls/slider-control/slider-control';
import {borderRadiusDescriptor, opacityDescriptor, paddingDescriptor, responsiveWidthDescriptor} from '../../controls/slider-control/slider-control-descriptors';
import {makeStyleHeader} from '../../controls/style-header/style-header';
import {makeModListControl} from '../../controls/mod-list-control/mod-list-control';
import {makeMediaModListDescriptor, makeVideoModListDescriptor} from '../../controls/mod-list-control/mod-list-control-descriptors';
import {LinkWrapperControl} from '../../controls/link-wrapper-control/link-wrapper-control';
import {makeCtaButtonControl} from '../../controls/cta-button-control/cta-button-control';
import {copyPasteStylesDescriptor} from '../../controls/cta-button-control/cta-button-control-descriptors';
import {isMediaComponent, registerEmailMakerSector} from '../../right-panel-registry';
import {TileComplexity} from '@EmailMakerReact/lib/email-maker-tile-meta-library.types';
import {deleteControl, duplicateControl, lockControl} from '../shared/common-action-controls';
import {borderStyleControl, conditionalBorderColorControl, conditionalBorderWidthControl} from '../shared/border-controls';
import {WARNING_INFO_BOX_VARIANT_CLASS} from '../shared/sector-predicates';
import type {EmailMakerControl, EmailMakerSectorVisibilityCtx} from '../../right-panel-registry.types';

const stylesHeader: EmailMakerControl = makeStyleHeader('pmwjs_styles');

const videoWarningControl = makeInfoBoxControl({
  contentKey: 'pmwjs_video_trimmed_warning',
  variantClass: WARNING_INFO_BOX_VARIANT_CLASS,
});
const imageModListControl = makeModListControl(makeMediaModListDescriptor);
const videoModListControl = makeModListControl(makeVideoModListDescriptor);
const urlControl: EmailMakerControl = LinkWrapperControl;
const copyPasteStylesControl = makeCtaButtonControl(copyPasteStylesDescriptor);

const horizontalAlignControl = makeOptionListControl(horizontalAlignDescriptor);

const opacityControl = makeSliderControl(opacityDescriptor);
const widthControl = makeSliderControl(responsiveWidthDescriptor);
const borderRadiusControl = makeSliderControl(borderRadiusDescriptor);
const paddingControl = makeSliderControl(paddingDescriptor);

/** Must be registered after all exact-type entries so PMW_DESIGN (which also has the media prop) wins over this. */
registerEmailMakerSector((component: Component) => isMediaComponent(component) && !component.is(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN), {
  title: (component: Component): string => {
    const type = component.get('type');
    return type === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO ? window.i18next.t('pmwjs_video') : window.i18next.t('pmwjs_image');
  },
  properties: [
    {control: duplicateControl},
    {control: lockControl},
    {control: deleteControl},
    {control: videoWarningControl, isVisible: ({component}: EmailMakerSectorVisibilityCtx): boolean => isVideoMediaComponent(component)},
    {control: videoModListControl, isVisible: ({component}: EmailMakerSectorVisibilityCtx): boolean => isVideoMediaComponent(component)},
    {
      control: imageModListControl,
      isVisible: ({component}: EmailMakerSectorVisibilityCtx): boolean => !isVideoMediaComponent(component),
    },
    {control: urlControl, actionMeta: {icon: 'icon-link', labelKey: 'pmwjs_link', complexity: TileComplexity.SIMPLE}},
    {control: stylesHeader},
    {control: copyPasteStylesControl},
    {control: opacityControl, tileMeta: {icon: 'icon-opacity', labelKey: 'pmwjs_opacity', complexity: TileComplexity.SIMPLE}},
    {control: widthControl, tileMeta: {icon: 'icon-stretch-horizontal', labelKey: 'pmwjs_width', complexity: TileComplexity.SIMPLE}},
    {control: horizontalAlignControl, tileMeta: {icon: 'icon-h-align-center', labelKey: 'pmwjs_horizontal_alignment', complexity: TileComplexity.SIMPLE}},
    {control: borderStyleControl, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_border', complexity: TileComplexity.SIMPLE}},
    {control: conditionalBorderColorControl, tileMeta: {icon: 'icon-colorpicker', labelKey: 'pmwjs_border_color', complexity: TileComplexity.SIMPLE}},
    {control: conditionalBorderWidthControl, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_thickness', complexity: TileComplexity.SIMPLE}},
    {control: borderRadiusControl, tileMeta: {icon: 'icon-corner', labelKey: 'pmwjs_roundness', complexity: TileComplexity.COMPLEX}},
    {control: paddingControl, tileMeta: {icon: 'icon-edge-spacing', labelKey: 'pmwjs_spacing', complexity: TileComplexity.COMPLEX}},
  ],
});
